'use client';

import { FC, useEffect, useState } from 'react';

import { User, UserGroups } from '@ravago/shared/authentication';
import { NormalizedProduct } from '@ravago/shared/page-data/models/contentful-mapped';
import { RequestMissingDocumentsButtonNode } from '@ravago/shared/page-data/models/elements';
import { RendererGlobals } from '@ravago/shared/page-data/models/utils';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { DropdownOption } from '@ravago/shared/radiance/components/input-field/components/Dropdown/Dropdown';
import notification from '@ravago/shared/radiance/utils/notification/notification';
import { useTranslations } from '@ravago/shared/translations/client';

import { useDialogContext } from '../../../../hooks/use-dialog-context';
import { useDialogFormContext } from '../../../../hooks/use-dialog-form-context';

interface Props {
   node: RequestMissingDocumentsButtonNode;
   url?: string;
   globals: RendererGlobals;
   productId: string;
   productName: string;
   categories: DropdownOption[];
   user?: User;
   accessToken?: string;
}

export const RequestMissingDocumentsButton: FC<Props> = ({ node, productName, productId, url, user, accessToken, categories, globals }) => {
   const translate = useTranslations();
   const {
      contentEntity,
      actions: { sendMissingDocumentForm }
   } = globals;
   const [TDurl, setTDurl] = useState(url);
   const { dispatch } = useDialogContext();
   const { customerFeedback } = useDialogFormContext();

   const product = contentEntity as NormalizedProduct;

   useEffect(() => {
      if (!product) return;

      if (customerFeedback.category?.id && customerFeedback.feedback && accessToken) {
         sendMissingDocumentForm(
            productId,
            customerFeedback.feedback ?? '',
            parseInt(customerFeedback.category.id, 10),
            false,
            product.isTailCatalog ? `${product.name} ${product.ulProductId ? `(UL id ${product.ulProductId})` : ''}` : undefined
         ).then((post) => {
            if (post) {
               notification(translate('request-missing-documents-form.sent').toString(), { duration: 5000 });
            }
         });
      }
   }, [customerFeedback, accessToken]);

   useEffect(() => {
      if (user?.mail) setTDurl(`${url}${user.mail}`);
   }, [url, user]);

   if (!product) return null;

   const verifiedUser =
      user && !(user.groups.includes(UserGroups.digitalPlatformGuest) || user.groups.includes(UserGroups.digitalPlatformPendingVerification));

   const handleRequestMissingDocuments = () => {
      if (user && (user.groups.includes(UserGroups.ravagoSalesRepresentative) || user.groups.includes(UserGroups.ravagoGlobalGroupAdmin))) {
         window.open(TDurl, '_blank');
      } else if (verifiedUser) {
         if (dispatch) {
            dispatch({
               type: 'open_dialog',
               value: {
                  dialogType: 'request-documents-form',
                  title: productName,
                  requestDocumentFormCategories: categories
               }
            });
         }
      }
   };

   return (
      <Button
         data-testid="missing-documents-button"
         trailingIcon={node.trailingIcon}
         leadingIcon={node.leadingIcon}
         disabled={globals.isConsumer && (!url || !categories?.length || !verifiedUser)}
         variant={node.variant}
         badge={node.badge}
         onClick={() => (globals.isConsumer ? handleRequestMissingDocuments() : undefined)}
      >
         {node.label.value}
      </Button>
   );
};
